import { useCallback, useMemo } from 'react';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Link, TextDisplay3, TextTitle4 } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, logClick, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { BaseL3Chain } from ':cloud/types/ts_types';
import { BASE_APPCHAINS_DOCS_LINK, BASE_APPCHAINS_WAITLIST_LINK } from ':cloud/utils/links';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import {
  KeyFeatureItem,
  KeyFeaturesSection,
} from ':cloud/widgets/sharedcomponents/KeyFeaturesSection';

import { FULL_PAGE_MAX_WIDTH } from '../product/constants';
import { PricingLandingTemplate } from '../product/PricingLandingTemplate';
import { PricingProps } from '../product/types';

import { DeploymentModal } from './DeploymentModal';
// import { Tutorials } from './Tutorials';

const keyFeatures: KeyFeatureItem[] = [
  {
    icon: <Icon name="lightningBolt" size="l" color="warning" />,
    title: 'High throughput, low costs',
    description:
      'Faster transactions with 1-second block times and under 10-second roundtrips — for fractions of cents.',
  },
  {
    icon: <Icon name="taxesReceipt" size="l" color="warning" />,
    title: 'Sustainable growth',
    description: 'Deliver delightful, performant user experiences with dedicated blockspace.',
  },
  {
    icon: <Icon name="cash" size="l" color="warning" />,
    title: 'Builder-friendly fees',
    description:
      'Competitive, fixed monthly price. No complicated vendor commitments, no price negotiations.',
  },
  {
    icon: <Icon name="rocket" size="l" color="warning" />,
    title: 'Enterprise-grade infra',
    description:
      'Fully-managed sequencers and nodes, maintenance and upgrades, and block explorer. Plus real-time monitoring and alerts for any disruptions in chain performance.',
  },
  {
    icon: <Icon name="globe" size="l" color="warning" />,
    title: 'Base builder tools',
    description:
      'Seamless integration with Smart Wallet, Paymaster, OnchainKit, AgentKit, and other tools from Base and Coinbase Developer Platform.',
  },
  {
    icon: <Icon name="perpetualSwap" size="l" color="warning" />,
    title: 'Open-source',
    description:
      'Built on the OP Enclave framework for fast withdrawals and a seamless integration between your Appchain and Base. ',
  },
];

const pricingSpecs: PricingProps[] = [
  {
    billingUnit: 'Testnet',
    price: 'Starting at $1/mo.',
  },
  {
    billingUnit: 'Mainnet',
    price: (
      <Link href={BASE_APPCHAINS_WAITLIST_LINK} target="_blank" underline={false}>
        Contact us
      </Link>
    ),
  },
];

type BaseAppchainEmptyStateProps = {
  chain?: BaseL3Chain;
};
export function BaseAppchainEmptyState({ chain }: BaseAppchainEmptyStateProps) {
  const { isDesktop } = useSimpleBreakpoints();
  const responsiveTemplateColumnsHeader = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  const [
    deploymentModalVisible,
    { toggleOn: showDeploymentModal, toggleOff: hideDeploymentModal },
  ] = useToggler(false);

  useLogViewOnMount(EventName.base_appchains_empty_state_view);

  const handleJoinWaitlistPress = useCallback(() => {
    logClick(EventName.base_appchains_join_waitlist_click);
    openUrlInNewWindow(BASE_APPCHAINS_WAITLIST_LINK);
  }, []);

  const handleDocsPress = useCallback(() => {
    logClick(EventName.base_appchains_view_docs_click);
    openUrlInNewWindow(BASE_APPCHAINS_DOCS_LINK);
  }, []);

  return (
    <Box display="block" width="100%" minWidth="100%" flexGrow={1}>
      <DeploymentModal
        isVisible={deploymentModalVisible}
        onClose={hideDeploymentModal}
        chain={chain}
      />
      <Box display="block" borderedBottom>
        <Grid
          spacing={8}
          width="100%"
          maxWidth={FULL_PAGE_MAX_WIDTH}
          templateColumns={responsiveTemplateColumnsHeader}
          gap={6}
        >
          <VStack maxWidth={500} gap={1}>
            <TextDisplay3 as="h1">Launch your own Base Appchain in minutes</TextDisplay3>
            <TextTitle4 spacingBottom={1} as="h4" color="foregroundMuted">
              Unlock fast and cheap transactions at scale, with dedicated blockspace and seamless
              integration with Base tools.
            </TextTitle4>
            <HStack gap={2}>
              {!chain ? (
                <Button onPress={handleJoinWaitlistPress}>Join waitlist</Button>
              ) : (
                <Button onPress={showDeploymentModal}>Deploy your appchain</Button>
              )}
              <Button
                variant="secondary"
                endIcon="diagonalUpArrow"
                onPress={handleDocsPress}
                accessibilityLabel="View docs"
              >
                View docs
              </Button>
            </HStack>
          </VStack>
          <Box justifyContent={isDesktop ? 'flex-end' : 'flex-start'}>
            <PricingLandingTemplate specs={pricingSpecs} maxWidth={350} pricingDescription="" />
          </Box>
        </Grid>
      </Box>

      <KeyFeaturesSection
        borderedBottom={false}
        sectionTitle="Why Base Appchains?"
        features={keyFeatures}
        gridColumns="1fr 1fr 1fr"
      />
      {/* TODO: add back when content is available */}
      {/* <Tutorials /> */}
    </Box>
  );
}
